import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  Box,
  ChevronsLeft,
  ChevronsRight,
  Compass,
  Gavel,
  Handshake,
  Home,
  Inbox,
  LayoutDashboard,
  LogOut,
  MessagesSquare,
  Settings,
  Shield,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import type { RootState } from "../../store/store";
import { userService } from "../../api/services/user.service";
import { updateProfile } from "../../store/authSlice";
import { useIsAdmin } from "../../hooks/useAuth";
import { useLogout } from "../../hooks/useLogout";
import { Brand } from "../ui/Brand";
import { cn } from "../../lib/utils";

const COLLAPSE_KEY = "flipiq.sidebar.collapsed";

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  isActive: (path: string) => boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const buildNav = (role: string | null, isAdmin: boolean, isAgent = false): NavGroup[] => {
  const groups: NavGroup[] = [
    {
      title: "Overview",
      items: [
        {
          label: "Dashboard",
          to: "/user",
          icon: <LayoutDashboard />,
          isActive: (p) => p === "/user",
        },
      ],
    },
  ];

  const buyGroup: NavGroup | null =
    role === "both" || role === "buyer"
      ? {
          title: "Buy",
      items: [
        {
          label: "Discover",
          to: "/buy",
          icon: <Compass />,
          isActive: (p) =>
            p === "/buy" ||
            (p.startsWith("/buy/") &&
              !p.startsWith("/buy/bids") &&
              !p.startsWith("/buy/buy-boxes") &&
              !p.startsWith("/buy/matches")),
        },
        {
          label: "My bids",
          to: "/buy/bids",
          icon: <Gavel />,
          isActive: (p) => p.startsWith("/buy/bids") || p === "/buy/counter-bid",
        },
        {
          label: "Buy boxes",
          to: "/buy/buy-boxes",
          icon: <Box />,
          isActive: (p) => p.startsWith("/buy/buy-boxes"),
        },
        {
          label: "Matches",
          to: "/buy/matches",
          icon: <Sparkles />,
          isActive: (p) => p === "/buy/matches",
        },
      ],
        }
      : null;

  // A buyer-only account can't sell yet, so it gets one entry into the seller setup
  // (/sell/add asks to turn on selling) instead of seller tools it can't use.
  const sellGroup: NavGroup =
    role === "buyer"
      ? {
          title: "Sell",
          items: [
            {
              label: "List a property",
              to: "/sell/add",
              icon: <Home />,
              isActive: (p) => p === "/sell" || p.startsWith("/sell/"),
            },
          ],
        }
      : {
          title: "Sell",
          items: [
            {
              label: "My listings",
              to: "/sell",
              icon: <Home />,
              // Includes the listing wizard (/sell/add), whose back link returns here
              isActive: (p) =>
                p === "/sell" ||
                (p.startsWith("/sell/") && !p.startsWith("/sell/offers") && !p.startsWith("/sell/listing/")),
            },
            {
              label: "Offers",
              to: "/sell/offers",
              icon: <Inbox />,
              isActive: (p) => p.startsWith("/sell/offers") || p.startsWith("/sell/listing/"),
            },
          ],
        };

  // The account's own side comes first: sellers see Sell above Buy, buyers Buy above Sell
  if (role === "seller") {
    groups.push(sellGroup);
    if (buyGroup) groups.push(buyGroup);
  } else {
    if (buyGroup) groups.push(buyGroup);
    groups.push(sellGroup);
  }

  const account: NavItem[] = [
    {
      label: "Inbox",
      to: "/user/inbox",
      icon: <MessagesSquare />,
      isActive: (p) => p.startsWith("/user/inbox"),
    },
    {
      label: "Deals",
      to: "/deals",
      icon: <Handshake />,
      isActive: (p) => p === "/deals" || p === "/user/deals",
    },
    {
      label: "Network",
      to: "/network",
      icon: <Users />,
      // Contacts (/user/contact/*) are the same people as the network, so they sit under it
      isActive: (p) => p.startsWith("/network") || p.startsWith("/user/network") || p.startsWith("/user/contact"),
    },
    // PRD 11.2: licensed agents' co-branded page and buyer catcher
    ...(isAgent
      ? [
          {
            label: "My agent page",
            to: "/agent-page",
            icon: <BadgeCheck />,
            isActive: (p: string) => p.startsWith("/agent-page"),
          },
        ]
      : []),
    {
      label: "Profile and settings",
      to: "/profile",
      icon: <Settings />,
      isActive: (p) => p.startsWith("/profile"),
    },
  ];
  if (isAdmin) {
    account.push({
      label: "Admin",
      to: "/admin",
      icon: <Shield />,
      isActive: (p) => p.startsWith("/admin"),
    });
  }
  groups.push({ title: "Workspace", items: account });

  return groups;
};

interface SidebarContentProps {
  collapsed: boolean;
  onToggleCollapse?: () => void;
  onNavigate?: () => void;
  onClose?: () => void;
}

/** Footer utilities share the nav items' size and alignment */
const footerItemClass =
  "flex h-8 w-full items-center gap-2.5 rounded-md text-sm text-gray-600 transition-colors hover:bg-gray-200/40 hover:text-gray-900 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-gray-400";

const SidebarContent: React.FC<SidebarContentProps> = ({
  collapsed,
  onToggleCollapse,
  onNavigate,
  onClose,
}) => {
  const { pathname } = useLocation();
  const { isAdmin } = useIsAdmin();
  const { logout } = useLogout();
  const role = useSelector((s: RootState) => s.auth.role);
  const { data: profile } = useQuery({
    queryKey: ["userProfile"],
    queryFn: userService.getProfile,
    staleTime: 5 * 60 * 1000,
  });
  const groups = buildNav(role, isAdmin, profile?.affiliateType === "AGENT");

  return (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className={cn("flex h-16 shrink-0 items-center", collapsed ? "justify-center px-2" : "justify-between px-5")}>
        <Brand to="/user" compact={collapsed} />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="size-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-3 pb-4 pt-3">
        {groups.map((group, gi) => (
          <div key={group.title} className={cn(gi > 0 && "mt-6")}>
            {collapsed ? (
              gi > 0 && <div className="mx-2 mb-3 h-px bg-gray-200" />
            ) : (
              <p className="mb-1 px-3 text-xs font-medium text-gray-500">
                {group.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.isActive(pathname);
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      title={collapsed ? item.label : undefined}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "group relative flex h-8 items-center gap-2.5 rounded-md text-sm transition-colors [&_svg]:size-4 [&_svg]:shrink-0",
                        collapsed ? "justify-center" : "px-3",
                        active
                          ? "bg-gray-200/70 font-medium text-gray-900"
                          : "text-gray-600 hover:bg-gray-200/40 hover:text-gray-900"
                      )}
                    >
                      <span className={cn(active ? "text-gray-900" : "text-gray-400 group-hover:text-gray-600")}>
                        {item.icon}
                      </span>
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer: identity lives in the top-bar account menu, so only utilities sit here */}
      <div className="shrink-0 space-y-0.5 border-t border-gray-200 px-3 py-3">
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : undefined}
            className={cn(footerItemClass, collapsed ? "justify-center" : "px-3")}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
            {!collapsed && <span className="truncate">Collapse</span>}
          </button>
        )}
        <button
          type="button"
          onClick={() => void logout()}
          title={collapsed ? "Log out" : undefined}
          aria-label="Log out"
          className={cn(footerItemClass, collapsed ? "justify-center" : "px-3")}
        >
          <LogOut />
          {!collapsed && <span className="truncate">Log out</span>}
        </button>
      </div>
    </div>
  );
};

interface SidebarVerifiedProps {
  /** Controlled mobile drawer state (owned by the layout so the top bar can open it) */
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

const SidebarVerified: React.FC<SidebarVerifiedProps> = ({ mobileOpen = false, onMobileOpenChange }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const role = useSelector((s: RootState) => s.auth.role);
  const firstName = useSelector((s: RootState) => s.auth.firstName);
  const lastName = useSelector((s: RootState) => s.auth.lastName);
  const photoUrl = useSelector((s: RootState) => s.auth.photoUrl);

  // Keep Redux in sync with the server profile (name, photo, role can change elsewhere)
  const { data: profile } = useQuery({
    queryKey: ["userProfile"],
    queryFn: userService.getProfile,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!profile) return;
    const updates: { firstName?: string; lastName?: string; photoUrl?: string | null; role?: string } = {};
    if (profile.firstName && profile.firstName !== firstName) updates.firstName = profile.firstName;
    if (profile.lastName && profile.lastName !== lastName) updates.lastName = profile.lastName;
    if (profile.photoUrl !== photoUrl) updates.photoUrl = profile.photoUrl;
    if (profile.role && profile.role !== role) updates.role = profile.role;
    if (Object.keys(updates).length > 0) dispatch(updateProfile(updates));
  }, [profile, firstName, lastName, photoUrl, role, dispatch]);

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // Storage can be unavailable (private mode); collapse still works for this session
      }
      return next;
    });
  };

  // Close the drawer when the route changes
  useEffect(() => {
    onMobileOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onMobileOpenChange?.(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen, onMobileOpenChange]);

  return (
    <>
      <aside
        className={cn(
          "hidden h-full shrink-0 flex-col border-r border-gray-200 bg-gray-50 transition-[width] duration-200 lg:flex",
          collapsed ? "w-[68px]" : "w-64"
        )}
      >
        <SidebarContent collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>

      {mobileOpen &&
        createPortal(
          <div className="fixed inset-0 z-[1000] lg:hidden">
            <div
              className="absolute inset-0 animate-fade-in bg-gray-950/40 backdrop-blur-[2px]"
              onClick={() => onMobileOpenChange?.(false)}
            />
            <aside
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
              className="absolute inset-y-0 left-0 w-72 max-w-[85vw] animate-rise border-r border-gray-200 bg-gray-50 shadow-overlay"
            >
              <SidebarContent
                collapsed={false}
                onNavigate={() => onMobileOpenChange?.(false)}
                onClose={() => onMobileOpenChange?.(false)}
              />
            </aside>
          </div>,
          document.body
        )}
    </>
  );
};

export default SidebarVerified;
